// BetaFeedback.js — "still being worked on · send feedback" for the Layered Read.
//
// A small trigger button opens a modal with a textarea. Submitting posts to the site's existing
// feature-request API (POST /feature-request/submit-api), which SAVES the request and EMAILS
// lemuel@scriptreader.ai — the same flow as the site-wide feature request form. The message is tagged
// [Layered Read · <title> #<id>] so it's obvious where the feedback came from.

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { tip } from './Tip';

const NAVY = '#14536e';

export default function BetaFeedback({ movieHash = null, movieTitle = null, style = {}, trigger = true, label = 'Beta — send feedback' }) {
  const [open, setOpen] = useState(false);
  // Opened from outside the React root too (the analysis page's Layered Read heading, 2026-10-24):
  // window event 'lr:feedback'. Mount ONE listener per page — Icicle mounts it with trigger={false}.
  useEffect(() => {
    if (trigger) return undefined;
    const onOpen = () => { setOpen(true); };
    window.addEventListener('lr:feedback', onOpen);
    return () => window.removeEventListener('lr:feedback', onOpen);
  }, [trigger]);
  const [text, setText] = useState('');
  const [state, setState] = useState('idle');   // idle | sending | done | error

  const close = () => { setOpen(false); setState('idle'); setText(''); };

  const submit = async () => {
    const t = text.trim();
    if (!t || state === 'sending') return;
    setState('sending');
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      const tag = `[Layered Read${movieTitle ? ' · ' + movieTitle : ''}${movieId ? ' #' + movieId : ''}]`;
      const r = await fetch('/feature-request/submit-api', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', ...(token ? { 'X-CSRF-TOKEN': token } : {}) },
        body: JSON.stringify({ feature_request: `${tag}\n${t}` }),
      });
      const j = await r.json().catch(() => ({}));
      if (r.ok && j.success !== false) setState('done');
      else setState('error');
    } catch (e) { setState('error'); }
  };

  return (
    <>
      {trigger && <button type="button" onClick={() => { setOpen(true); setState('idle'); }} {...tip("This is a beta — tell us what's broken or missing")}
        style={{ all: 'unset', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5,
          fontSize: 12.5, fontWeight: 600, color: NAVY, ...style }}>
        🛠 <span style={{ textDecoration: 'underline', textUnderlineOffset: 3 }}>{label}</span>
      </button>}

      {open && (
        <div onClick={close}
          style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(15,23,42,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ width: 'min(520px, 96vw)', background: '#fff', borderRadius: 14, boxShadow: '0 12px 40px rgba(15,23,42,.35)', overflow: 'hidden' }}>
            <div style={{ padding: '16px 18px', borderBottom: '1px solid #eef2f5' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <span style={{ background: '#b8860b', color: '#fff', fontWeight: 800, fontSize: 10.5, letterSpacing: '.06em', borderRadius: 4, padding: '2px 7px' }}>BETA</span>
                <span style={{ fontWeight: 800, color: '#0f172a', fontSize: '1.05rem' }}>Still being worked on</span>
              </div>
              <div style={{ color: '#475569', fontSize: '.9rem', lineHeight: 1.5, marginTop: 6 }}>
                Hit a bug, something confusing, or a feature you wish it had? Tell us — it goes straight to the team.
              </div>
            </div>

            {state === 'done' ? (
              <div style={{ padding: '22px 18px', textAlign: 'center' }}>
                <div style={{ fontSize: '1.05rem', fontWeight: 700, color: NAVY }}>Thanks — sent! 🎉</div>
                <div style={{ color: '#64748b', fontSize: '.9rem', marginTop: 6 }}>We read every note.</div>
                <button type="button" onClick={close}
                  style={{ marginTop: 14, cursor: 'pointer', border: 'none', background: NAVY, color: '#fff', borderRadius: 8, padding: '.5rem 1.1rem', fontWeight: 700, fontSize: '.9rem' }}>Close</button>
              </div>
            ) : (
              <div style={{ padding: '14px 18px 16px' }}>
                <textarea value={text} onChange={e => setText(e.target.value)} autoFocus rows={5}
                  placeholder="What’s broken, confusing, or missing?"
                  style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #cbd5e1', borderRadius: 10, padding: '10px 12px', fontSize: '.95rem', lineHeight: 1.5, resize: 'vertical', color: '#0f172a' }} />
                {state === 'error' && <div style={{ color: '#b3261e', fontSize: '.85rem', marginTop: 6 }}>Couldn’t send that — please try again.</div>}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                  <button type="button" onClick={close}
                    style={{ cursor: 'pointer', border: '1px solid #cbd5e1', background: '#fff', color: '#475569', borderRadius: 8, padding: '.5rem 1rem', fontWeight: 600, fontSize: '.9rem' }}>Cancel</button>
                  <button type="button" onClick={submit} disabled={!text.trim() || state === 'sending'}
                    style={{ cursor: (!text.trim() || state === 'sending') ? 'default' : 'pointer', border: 'none',
                      background: (!text.trim() || state === 'sending') ? '#9fb3c0' : NAVY, color: '#fff', borderRadius: 8, padding: '.5rem 1.2rem', fontWeight: 700, fontSize: '.9rem' }}>
                    {state === 'sending' ? 'Sending…' : 'Send'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

BetaFeedback.propTypes = {
  movieHash: PropTypes.string,
  movieTitle: PropTypes.string,
  style: PropTypes.object,
};
